import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { AuthLayout } from '../components/AuthLayout';
import { SocialAuthButtons } from '../components/SocialAuthButtons';

export function SignInPage() {
  const { t } = useTranslation('auth');
  const { isAuthenticated, isLoading, signInWithEmail, signInWithGoogle, signInWithGitHub } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const result = await signInWithEmail(email, password);
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err: any) {
      setError(err.message || t('signIn.failedDefault'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="text-2xl font-bold text-gray-900">{t('signIn.title')}</h1>
      <p className="mt-2 text-sm text-gray-500">
        {t('signIn.subtitle')}
      </p>

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {t('signIn.email')}
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder={t('signIn.emailPlaceholder')}
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              {t('signIn.password')}
            </label>
            <Link to="/forgot-password" className="text-sm font-medium text-primary-600 hover:text-primary-500">
              {t('signIn.forgotPassword')}
            </Link>
          </div>
          <input
            id="password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder={t('signIn.passwordPlaceholder')}
          />
        </div>

        <button
          type="submit"
          disabled={submitting || isLoading}
          className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
        >
          {submitting ? t('signIn.submitting') : t('signIn.submit')}
        </button>
      </form>

      <SocialAuthButtons onGoogle={signInWithGoogle} onGitHub={signInWithGitHub} disabled={submitting} />

      <p className="mt-6 text-center text-sm text-gray-500">
        {t('signIn.noAccount')}{' '}
        <Link to="/signup" className="font-semibold text-primary-600 hover:text-primary-500">
          {t('signIn.signUpLink')}
        </Link>
      </p>
    </AuthLayout>
  );
}
